import DashboardLayout from "@/views/Layout/DashboardLayout.vue";
import AuthLayout from "@/views/Layout/AuthLayout.vue";
import NotFound from "@/views/NotFoundPage.vue";
import Auth from "@/services/Auth";
import { localDB } from "@/services/localDB";
import DB from "@/services/DB";

// ===== IMPORT GLOBAL ROUTE CONSTANTS =====
import {
  SHARED_ROUTES,
  MEMBER_ONLY_ROUTES,
  PARTNER_ONLY_ROUTES,
  PARTNER_ALLOWED_ROUTES,
  isSharedRoute,
  isMemberOnlyRoute,
  isPartnerAllowedRoute,
  getDefaultRedirectPath,
} from "@/constants/routeAccess";

// ===== IMPROVED ROLE GUARDS =====

// Global role guard function dengan improved logic menggunakan konstanta global
const roleGuard = (to, from, next) => {
  const role = localStorage.getItem("role") || "member";
  const token = localStorage.getItem("token");

  console.log(`🛡️ Route guard: ${from.path} -> ${to.path}, role: ${role}`);

  // Jika tidak ada token, redirect ke login
  if (!token) {
    console.log("🛡️ No token found, redirecting to login");
    return next("/login");
  }

  // Check if route is shared (accessible by both roles)
  if (isSharedRoute(to.path)) {
    console.log(`🛡️ Shared route accessed by ${role}: ${to.path}`);
    return next();
  }

  if (role === "partner") {
    // Check if trying to access member-only route
    if (isMemberOnlyRoute(to.path)) {
      console.log(
        `🛡️ Partner blocked from member-only route: ${
          to.path
        }, redirecting to ${getDefaultRedirectPath("partner")}`
      );
      return next(getDefaultRedirectPath("partner"));
    }

    // Check if route is allowed for partners
    if (!isPartnerAllowedRoute(to.path)) {
      console.log(
        `🛡️ Partner blocked from restricted route: ${
          to.path
        }, redirecting to ${getDefaultRedirectPath("partner")}`
      );
      return next(getDefaultRedirectPath("partner"));
    }
  }

  // For member role or allowed routes, proceed
  console.log(`🛡️ Route guard passed for ${role}: ${to.path}`);
  next();
};

// Utility functions untuk create specific role guards
const createRoleGuard = (
  allowedRoles = ["member"],
  redirectPath = "/account"
) => {
  return (to, from, next) => {
    const role = localStorage.getItem("role") || "member";
    const token = localStorage.getItem("token");

    if (!token) {
      console.log("🛡️ No token in role guard, redirecting to login");
      return next("/login");
    }

    if (!allowedRoles.includes(role)) {
      console.log(
        `🛡️ Role ${role} not allowed for ${to.path}, redirecting to ${redirectPath}`
      );
      return next(redirectPath);
    }

    console.log(`🛡️ Role guard passed: ${role} can access ${to.path}`);
    next();
  };
};

// Helper untuk create shared route guard (both member and partner can access)
const createSharedGuard = () => {
  return (to, from, next) => {
    const token = localStorage.getItem("token");
    const role = localStorage.getItem("role") || "member";

    if (!token) {
      console.log("🛡️ No token in shared guard, redirecting to login");
      return next("/login");
    }

    console.log(`🛡️ Shared route accessed by ${role}: ${to.path}`);
    next();
  };
};

// ===== ROUTES DEFINITION =====
const routes = [
  {
    path: "/",
    redirect: "edit-profile",
    component: DashboardLayout,
    beforeEnter: roleGuard, // Apply global role guard to all dashboard routes
    children: [
      // ===== SYSTEM ROUTES =====
      {
        path: "/suspend",
        name: "suspend",
        component: () => import("../views/Suspend.vue"),
      },

      // ===== MEMBER-ONLY ROUTES =====
      {
        path: "/kyc",
        name: "kyc",
        component: () => import("../views/Kyc.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/deposit",
        name: "deposit",
        component: () => import("../views/NewDeposit.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/choose-card",
        name: "choose-card",
        component: () => import("../views/New-Deposit/ChooseCard.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/deposit-method",
        name: "deposit-method",
        props: (route) => ({
          card_number: route.query.card_number,
        }),
        component: () => import("../views/New-Deposit/DepositMethod.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/digital-assets",
        name: "digital-assets",
        props: (route) => ({
          card_number: route.query.card_number,
          network: route.query.network,
        }),
        component: () => import("../views/New-Deposit/DigitalAssets.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/alipay",
        name: "alipay",
        props: (route) => ({
          card_number: route.query.card_number,
        }),
        component: () => import("../views/New-Deposit/Alipay.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/wechatpay",
        name: "wechatpay",
        props: (route) => ({
          card_number: route.query.card_number,
        }),
        component: () => import("../views/New-Deposit/Wechatpay.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/change-atm-pin",
        name: "change-atm-pin",
        props: (route) => ({
          card_number: route.query.card_number,
        }),
        component: () => import("../views/ChangeAtmPin.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/card-replacement/:id",
        name: "card-replacement",
        component: () => import("../views/CardReplacement.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/card-management",
        name: "card-management",
        component: () => import("../views/Card-Management/zero-new.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/order-card",
        name: "order-card",
        props: (route) => ({
          relation: route.query.relation,
          relation_id: route.query.relation_id,
          name: route.query.name,
        }),
        component: () =>
          import("../views/New-Card-Management/OrderNewCard.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/fill-in-details",
        name: "fill-in-details",
        props: (route) => ({
          relation: route.query.relation,
          relation_id: route.query.relation_id,
          name: route.query.name,
        }),
        component: () => import("../views/New-Card-Management/ApplyInfo.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/payment-method",
        name: "payment-method",
        props: (route) => ({
          relation: route.query.relation,
          relation_id: route.query.relation_id,
          name: route.query.name,
        }),
        component: () =>
          import("../views/New-Card-Management/PaymentMethod.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/application-fee",
        name: "application-fee",
        props: (route) => ({
          relation: route.query.relation,
          relation_id: route.query.relation_id,
          name: route.query.name,
        }),
        component: () =>
          import("../views/New-Card-Management/ApplicationFee.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },

      // ===== TRANSFER ROUTES (MEMBER ONLY) =====
      {
        path: "/transfer",
        name: "transfer",
        component: () => import("../views/Transfer/transfer.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/transfer-choose-card",
        name: "transfer-choose-card",
        component: () => import("../views/Transfer/chooseCard.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/transfer-method",
        name: "transfer-method",
        props: (route) => ({
          card_number: route.query.card_number,
          currency: route.query.currency,
        }),
        component: () => import("../views/Transfer/transferMethod.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/transfer-type",
        name: "transfer-type",
        props: (route) => ({
          card_number: route.query.card_number,
          currency: route.query.currency,
        }),
        component: () => import("../views/Transfer/transferType.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/TT-same-name",
        name: "TT-same-name",
        props: (route) => ({
          card_number: route.query.card_number,
        }),
        component: () => import("../views/Transfer/TTSameName.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/TT-third-party",
        name: "TT-third-party",
        props: (route) => ({
          card_number: route.query.card_number,
        }),
        component: () => import("../views/Transfer/TTThirdParty.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/cc",
        name: "cc",
        props: (route) => ({
          card_number: route.query.card_number,
        }),
        component: () => import("../views/Transfer/cc.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/summary",
        name: "summary",
        component: () => import("../views/Transfer/summary.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/summarycc",
        name: "summarycc",
        component: () => import("../views/Transfer/summarycc.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/transfer-details",
        name: "transfer-details",
        props: (route) => ({
          trans_no: route.query.trans_no,
          type: route.query.type,
        }),
        component: () => import("../views/Transfer/transferDetails.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/deposit-details",
        name: "deposit-details",
        props: (route) => ({
          trans_no: route.query.trans_no,
        }),
        component: () => import("../views/New-Deposit/depositDetails.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },

      // ===== SHARED ROUTES (BOTH MEMBER & PARTNER) =====
      {
        path: "/qr-transaction",
        name: "qr-transaction",
        component: () =>
          import("../views/QRTransaction/QRTransactionWrapper.vue"),
        beforeEnter: createSharedGuard(),
      },
      {
        path: "/qr-scan",
        name: "qr-scan",
        component: () => import("../views/QRTransaction/QRScan.vue"),
        beforeEnter: createSharedGuard(),
      },
      {
        path: "/qr-details",
        name: "qr-details",
        component: () => import("../views/QRTransaction/QRDetailsWrapper.vue"),
        beforeEnter: createSharedGuard(),
      },
      {
        path: "/qr-choose-card",
        name: "qr-choose-card",
        component: () => import("../views/QRTransaction/qrChooseCard.vue"),
        beforeEnter: createSharedGuard(),
      },
      {
        path: "/transaction-info",
        name: "transaction-info",
        component: () => import("../views/QRTransaction/transactionInfo.vue"),
        beforeEnter: createSharedGuard(),
      },
      {
        path: "/scan-qr",
        name: "scan-qr",
        props: (route) => ({
          trans_no: route.query.trans_no,
        }),
        component: () => import("../views/QRTransaction/scanQR.vue"),
        beforeEnter: createSharedGuard(),
      },

      // ===== PROFILE & SETTINGS ROUTES (SHARED) =====
      {
        path: "/edit-profile",
        name: "edit-profile",
        component: () => import("../views/EditProfile.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/system-configuration",
        name: "system-configuration",
        component: () => import("../views/Settings/SystemConfiguration.vue"),
        beforeEnter: createSharedGuard(),
      },
      {
        path: "/change-password",
        name: "change-password",
        component: () => import("../views/Settings/ChangePassword.vue"),
        beforeEnter: createSharedGuard(),
      },

      // ===== REPORT ROUTES =====
      {
        path: "/deposit-report",
        name: "deposit-report",
        component: () => import("../views/DepositReport.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/transfer-report",
        name: "transfer-report",
        component: () => import("../views/TransferReport.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/request-employee",
        name: "request-employee",
        props: (route) => ({
          id: route.query.id,
        }),
        component: () => import("../views/Account/RequestEmployee.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/account-information",
        name: "account-information",
        props: (route) => ({
          name: route.query.name,
          email: route.query.email,
          dob: route.query.dob,
          relation: route.query.relation,
          phone_code: route.query.phone_code,
          phone: route.query.phone,
          doc_type: route.query.doc_type,
          error_name: route.query.error_name,
          error_email: route.query.error_email,
          error_dob: route.query.error_dob,
          error_relation: route.query.error_relation,
          error_phone_code: route.query.error_phone_code,
          error_phone: route.query.error_phone,
        }),
        component: () => import("../views/Account/AccountInformation.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },

      {
        path: "/corporate-company-information",
        name: "corporate-company-information",
        component: () =>
          import("../views/Account/CorporateCompanyInformation.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/director-information",
        name: "director-information",
        component: () =>
          import("../views/Account/CorporateDirectorInformation.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/corporate-beneficiary",
        name: "corporate-beneficiary",
        component: () =>
          import("../views/Account/CorporateBeneficiaryInformation.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/corporate-summary",
        name: "corporate-summary",
        component: () => import("../views/Account/CorporateSummary.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/document-verification",
        name: "document-verification",
        props: (route) => ({
          name: route.query.name,
          email: route.query.email,
          dob: route.query.dob,
          relation: route.query.relation,
          phone_code: route.query.phone_code,
          phone: route.query.phone,
          doc_type: route.query.doc_type,
        }),
        component: () => import("../views/Account/DocumentVerification.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/account-summary",
        name: "account-summary",
        props: (route) => ({
          name: route.query.name,
          email: route.query.email,
          dob: route.query.dob,
          relation: route.query.relation,
          phone_code: route.query.phone_code,
          phone_country: route.query.phone_country,
          phone: route.query.phone,
          doc_type: route.query.doc_type,
        }),
        component: () => import("../views/Account/AccountSummary.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/wallet-report",
        name: "wallet-report",
        component: () => import("../views/WalletReport.vue"),
        beforeEnter: createSharedGuard(), // Both roles can access wallet report
      },

      // ===== PARTNER-SPECIFIC ROUTES dengan role guards =====
      {
        path: "/withdrawal",
        name: "withdrawal",
        component: () => import("../views/partner/WithdrawalWrapper.vue"),
        beforeEnter: createRoleGuard(["partner"], "/transaction"),
      },
      {
        path: "/withdrawal-report",
        name: "withdrawal-report",
        component: () => import("../views/partner/WithdrawalReport.vue"),
        beforeEnter: createRoleGuard(["partner"], "/transaction"),
      },
      {
        path: "/qr-transaction-report",
        name: "qr-transaction-report",
        component: () => import("../views/partner/QrTransactionReport.vue"),
        beforeEnter: createRoleGuard(["partner"], "/transaction"),
      },
      {
        path: "/withdrawal-topayment-type",
        name: "withdrawal-topayment-type",
        props: (route) => ({
          wallet_id: route.query.wallet_id,
          currency: route.query.currency,
        }),
        component: () => import("../views/partner/WithdrawalToPaymentType.vue"),
        beforeEnter: createRoleGuard(["partner"], "/transaction"),
      },
      {
        path: "/withdrawal-topay-application-samename",
        name: "withdrawal-topay-application-samename",
        props: (route) => ({
          wallet_id: route.query.wallet_id,
          currency: route.query.currency,
        }),
        component: () =>
          import("../views/partner/WithdrawalToPayApplicationSameName.vue"),
        beforeEnter: createRoleGuard(["partner"], "/transaction"),
      },
      {
        path: "/withdrawal-topay-application-thirdparty",
        name: "withdrawal-topay-application-thirdparty",
        props: (route) => ({
          wallet_id: route.query.wallet_id,
          currency: route.query.currency,
        }),
        component: () =>
          import("../views/partner/WithdrawalToPayApplicationThirdParty.vue"),
        beforeEnter: createRoleGuard(["partner"], "/transaction"),
      },
      {
        path: "/withdrawal-va",
        name: "withdrawal-va",
        component: () => import("../views/partner/WithdrawalToVa.vue"),
        beforeEnter: createRoleGuard(["partner"], "/transaction"),
      },
      {
        path: "/withdrawal-tocard",
        name: "withdrawal-tocard",
        component: () => import("../views/partner/WithdrawalToCard.vue"),
        beforeEnter: createRoleGuard(["partner"], "/transaction"),
      },
      {
        path: "/withdrawal-topayment",
        name: "withdrawal-topayment",
        component: () => import("../views/partner/WithdrawalToPayment.vue"),
        beforeEnter: createRoleGuard(["partner"], "/transaction"),
      },
      {
        path: "/withdrawal-method",
        name: "withdrawal-method",
        component: () => import("../views/partner/WithdrawalMethod.vue"),
        beforeEnter: createRoleGuard(["partner"], "/transaction"),
      },
      {
        path: "/withdrawal-tocard-application",
        name: "withdrawal-tocard-application",
        props: (route) => ({
          card_number: route.query.card_number,
          currency: route.query.currency,
        }),
        component: () =>
          import("../views/partner/WithdrawalToCardApplication.vue"),
        beforeEnter: createRoleGuard(["partner"], "/transaction"),
      },
      {
        path: "/withdrawal-topay-application",
        name: "withdrawal-topay-application",
        props: (route) => ({
          wallet_id: route.query.wallet_id,
          currency: route.query.currency,
        }),
        component: () =>
          import("../views/partner/WithdrawalToPayApplication.vue"),
        beforeEnter: createRoleGuard(["partner"], "/transaction"),
      },

      // ===== ACCOUNT ROUTES (ACCESSIBLE BY BOTH MEMBER & PARTNER) =====
      {
        path: "/account",
        name: "account",
        component: () => import("../views/Account/AccountWrapper.vue"),
        beforeEnter: createSharedGuard(), // ✅ BOTH member and partner can access
        // Wrapper will handle different views based on role:
        // - Member: src/views/Account.vue
        // - Partner: src/components/Account/PartnerProfile.vue
      },
      {
        path: "/transaction",
        name: "transaction",
        component: () => import("../views/Transaction.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/choose-type/:type",
        name: "choose-type",
        component: () => import("../views/ChooseType.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },

      // ===== COMPANY INFORMATION ROUTES (MEMBER ONLY) =====
      // International
      {
        path: "/international-company-information",
        name: "international-company-information",
        component: () =>
          import("../views/Account/International/CompanyInformation.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/international-business-address",
        name: "international-business-address",
        component: () =>
          import("../views/Account/International/BusinessAddress.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/international-registration-address",
        name: "international-registration-address",
        component: () =>
          import("../views/Account/International/RegistrationAddress.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/international-director-information",
        name: "international-director-information",
        component: () =>
          import("../views/Account/International/DirectorInformation.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/international-beneficiary-information",
        name: "international-beneficiary-information",
        component: () =>
          import("../views/Account/International/BeneficiaryInformation.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },

      // Hong Kong
      {
        path: "/hongkong-company-information",
        name: "hongkong-company-information",
        component: () =>
          import("../views/Account/Hongkong/CompanyInformation.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/hongkong-registered-address",
        name: "hongkong-registered-address",
        component: () =>
          import("../views/Account/Hongkong/RegisteredAddress.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/hongkong-director-information",
        name: "hongkong-director-information",
        component: () =>
          import("../views/Account/Hongkong/DirectorInformation.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/hongkong-beneficiary-information",
        name: "hongkong-beneficiary-information",
        component: () =>
          import("../views/Account/Hongkong/BeneficiaryInformation.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },

      // Domestic
      {
        path: "/domestic-company-information",
        name: "domestic-company-information",
        component: () =>
          import("../views/Account/Domestic/CompanyInformation.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/domestic-legal-information",
        name: "domestic-legal-information",
        component: () =>
          import("../views/Account/Domestic/LegalInformation.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/domestic-registered-address",
        name: "domestic-registered-address",
        component: () =>
          import("../views/Account/Domestic/RegisteredAddress.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },
      {
        path: "/domestic-beneficiary-information",
        name: "domestic-beneficiary-information",
        component: () =>
          import("../views/Account/Domestic/BeneficiaryInformation.vue"),
        beforeEnter: createRoleGuard(["member"], "/account"),
      },

      // ===== SUPPORT ROUTES (SHARED) =====
      {
        path: "/cs",
        name: "customer-service",
        component: () => import("../views/CustomerService.vue"),
        beforeEnter: createSharedGuard(),
      },
      {
        path: "/faq",
        name: "faq",
        component: () => import("../views/Faq.vue"),
        beforeEnter: createSharedGuard(),
      },
      // {
      //   path: '/card-tracking',
      //   name: 'card-tracking',
      //   component: () => import('../views/Shipping.vue')
      // },

      // ===== LOGOUT ROUTE =====
      {
        path: "/logout",
        name: "logout",
        component: {
          async beforeRouteEnter(to, from, next) {
            try {
              console.log("🚪 Starting logout process...");

              // Clear localDB tables
              try {
                await localDB.clear("domestic");
                await localDB.clear("hongkong");
                await localDB.clear("international");
                console.log("✅ LocalDB cleared successfully");
              } catch (error) {
                console.warn("⚠️ Error clearing localDB:", error.message);
              }

              // Safe remove with error handling
              try {
                await DB.remove("companyInformation");
                console.log("✅ Company information removed successfully");
              } catch (error) {
                console.warn(
                  "⚠️ Error removing company information:",
                  error.message
                );
              }

              // Clear localStorage items
              let currentToken = localStorage.getItem("notification_token");
              localStorage.removeItem("member");
              localStorage.removeItem("isFirstTime");

              // Handle logout API call
              if (currentToken) {
                try {
                  const response = await Auth.logout({
                    fcm_token: currentToken,
                  });
                  if (response.status == 200) {
                    localStorage.removeItem("notification_token");
                    localStorage.removeItem("token");
                    console.log("✅ Logout API call successful");
                  }
                } catch (error) {
                  console.warn("⚠️ Error during logout API call:", error);
                  // Remove tokens anyway
                  localStorage.removeItem("notification_token");
                  localStorage.removeItem("token");
                }
              } else {
                // No token found, proceed with localStorage cleanup
                localStorage.removeItem("token");
                console.log("✅ No FCM token found, cleared auth token");
              }

              // Reset role ke member saat logout dan load CSS
              console.log("🔄 Resetting role to member for logout");
              localStorage.setItem("role", "member");
              if (typeof window.loadRoleCss === "function") {
                window.loadRoleCss("member");
              }

              console.log("🚪 Logout process completed, reloading...");
              // Reload page
              location.reload();
            } catch (error) {
              console.error("❌ Error during logout process:", error);
              // Force logout anyway by clearing storage and reloading
              console.log("🔄 Force logout - clearing all data");

              localStorage.setItem("role", "member");
              if (typeof window.loadRoleCss === "function") {
                window.loadRoleCss("member");
              }

              localStorage.clear();
              location.reload();
            }
          },
        },
      },

      // {
      //   path: "/network-address",
      //   name: "network-address",
      //   component: () => import('../views/NetworkAddress/NetworkAddress.vue')
      // },
      // {
      //   path: "/network-address/create",
      //   name: "create-network-address",
      //   component: () => import('../views/NetworkAddress/NetworkAddressForm.vue')
      // }
    ],
  },

  // ===== AUTH LAYOUT ROUTES =====
  {
    path: "/",
    redirect: "login",
    component: AuthLayout,
    children: [
      {
        path: "/login",
        name: "login",
        meta: {
          noBodyBackground: true,
        },
        props: (route) => ({
          token: route.query.verification,
          register: route.query.register,
        }),
        component: () => import("../views/Auth/Login.vue"),
      },
      {
        path: "/register",
        name: "register",
        meta: {
          noBodyBackground: true,
        },
        component: () => import("../views/Auth/Register.vue"),
      },
      {
        path: "/forgot-password",
        name: "forgot-password",
        meta: {
          noBodyBackground: true,
        },
        component: () => import("../views/Auth/ForgotPassword.vue"),
      },
      {
        path: "/resend-email",
        name: "resend-email",
        meta: {
          noBodyBackground: true,
        },
        component: () => import("../views/Auth/ResendEmail.vue"),
      },
      {
        path: "/term-condition",
        name: "term-condition",
        meta: {
          noBodyBackground: true,
        },
        component: () => import("../views/TermCondition.vue"),
      },
      {
        path: "/privacy-policy-statement",
        name: "privacy-policy-statement",
        meta: {
          noBodyBackground: true,
        },
        component: () => import("../views/PrivacyPolicy.vue"),
      },
      {
        path: "/maintenance",
        name: "maintenances",
        component: () => import("../views/Maintenance.vue"),
      },
      {
        path: "/payment",
        name: "payment",
        meta: {
          noBodyBackground: true,
        },
        component: () => import("../views/PendingPayment.vue"),
      },

      // ===== 404 CATCH ALL =====
      {
        path: "*",
        component: NotFound,
      },
    ],
  },
];

export default routes;
